import React, { SFC, useEffect, useRef, RefObject, useState, } from "react";

// css
import "./style.scss";

/**
 * Props interface for the Prompt component
 * @interface PromptProps
 * @property {string} [prompt] - The prompt text to display before the input (e.g., "$> ")
 * @property {Array<{command: string, action: string, user: string[]}>} [commands] - Array of valid commands and their actions
 * @property {string} [className] - Optional CSS class for styling
 * @property {boolean} [disabled] - Whether the prompt is disabled
 * @property {string[]} [visibleTo] - Optional array of user roles that can see this prompt
 * @property {(command: string, action: string, user: string[]) => void} [onCommand] - Callback when a command is entered
 * @property {() => void} [onEscape] - Callback when escape key is pressed
 * @property {() => void} [onRendered] - Optional callback when component is rendered
 */
export interface PromptProps {
    prompt?: string;
    commands?: any[];
    className?: string;
    disabled?: boolean;
    visibleTo?: string[];

    onCommand?: (command: string, action: string, user: string[]) => void;
    onEscape?: () => void;
    onRendered?: () => void;
}

/** Default prompt string */
export const PROMPT_DEFAULT = "$> ";

/**
 * A command prompt component that simulates a terminal input.
 * Supports configurable commands, keyboard input handling, and command validation.
 * Only accepts alphanumeric characters, spaces, and common punctuation.
 * 
 * @component
 * @example
 * ```tsx
 * <Prompt 
 *   prompt="$> "
 *   commands={[
 *     { command: 'help', action: 'SHOW_HELP', user: ['admin'] }
 *   ]}
 *   onCommand={(cmd, action, user) => handleCommand(cmd, action, user)}
 * />
 * ```
 */
const Prompt: SFC<PromptProps> = (props) => {
    const { disabled, prompt, className, commands, onCommand, onRendered, } = props;
    /** Reference to the input span element */
    const ref: RefObject<HTMLSpanElement> = useRef();
    const css = [
        "__prompt__",
        disabled ? "disabled" : null,
        className ? className : null,
    ].join(" ").trim();

    /** Current input value state */
    const [value, setValue] = useState("");

    /**
     * Focuses the input span element
     */
    const handleFocus = () => ref.current.focus();

    /**
     * Processes the entered command
     * Finds matching command in the commands array and triggers onCommand callback
     */
    const handleCommand = () => {
        if (!onCommand) {
            return;
        }

        let command = commands.find(element => element.command === value);
        if (command === undefined) {
            command = commands.find(element => element.command === 'error');
        }

        setValue("");

        if (command) {
            // console.log("sd", value, command.action, user)
            onCommand(value, command.action, command.user);
        }
    };

    /**
     * Handles keyboard input events
     * @param {KeyboardEvent} e - The keyboard event
     */
    const handleKeyDown = (e: KeyboardEvent) => {
        if (disabled) {
            setValue("");
            return;
        }

        e.preventDefault();

        const key = e.key.toLowerCase();
        switch (key) {
            case "backspace":
                value.length && setValue(value.slice(0, -1));
                break;

            case "enter":
                handleCommand();
                break;

            default:
                // support alphanumeric, space, and limited puntuation only
                const re = /[a-z0-9,.<>/?[\]{}'";:*&^%$#@!~]/
                if (key.length === 1 && key.match(re)) {
                    setValue(value + key);
                }
                break;
        }
    };

    /**
     * Sets up event listeners on mount and cleans up on unmount
     */
    useEffect(() => {
        // mount
        onRendered && onRendered();
        document.addEventListener("keydown", handleKeyDown);

        // unmount
        return () => document.removeEventListener("keydown", handleKeyDown);
    });

    return (
        <div className={css} onClick={handleFocus}>
            {prompt && <span className={"prompt"}>{prompt}</span>}
            <span className={"input"} ref={ref}>{value}</span>
        </div>
    );
};

export default Prompt;
